// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import Latex from '../Latex.js';
import { productColor } from '../designSystem.js';
import { Heading, SubHeading } from '../Heading.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

// this component exists to gather together the three types of skills
export function EANSkillSection(props: {|
  children:
    | [
        Element<typeof EANPrerequisiteSkills>,
        Element<typeof EANMainSkills>,
        Element<typeof EANExtensionSkills>,
      ]
    | [Element<typeof EANMainSkills>, Element<typeof EANExtensionSkills>]
    | [Element<typeof EANPrerequisiteSkills>, Element<typeof EANMainSkills>]
    | [Element<typeof EANMainSkills>],
|}) {
  const { failedValidation } = usePropsValidation(props, EANSkillSection);
  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey }}>
      <Heading>Understanding and skills</Heading>
      {props.children}
    </div>
  );
}
EANSkillSection.spec = {
  children: t.exactOrderChildrenArray(
    t.optional(t.elementType(EANPrerequisiteSkills)),
    t.elementType(EANMainSkills),
    t.optional(t.elementType(EANExtensionSkills)),
  ),
};

export function EANPrerequisiteSkills(props: {|
  children: ChildrenArray<Element<typeof EANSkill>>,
|}) {
  const { failedValidation } = usePropsValidation(props, EANPrerequisiteSkills);
  if (failedValidation) return null;
  return (
    <div style={{ paddingTop: 12 }}>
      <SubHeading>Will use</SubHeading>
      <div style={{ height: 6 }} />
      {props.children}
    </div>
  );
}
EANPrerequisiteSkills.spec = {
  children: t.childrenArray(t.elementType(EANSkill)),
};

export function EANMainSkills(props: {|
  children: ChildrenArray<Element<typeof EANSkill>>,
|}) {
  const { failedValidation } = usePropsValidation(props, EANMainSkills);
  if (failedValidation) return null;
  return (
    <div style={{ paddingTop: 12 }}>
      <SubHeading>Will develop</SubHeading>
      <div style={{ height: 6 }} />
      {props.children}
    </div>
  );
}
EANMainSkills.spec = {
  children: t.childrenArray(t.elementType(EANSkill)),
};

export function EANExtensionSkills(props: {|
  children: ChildrenArray<Element<typeof EANSkill>>,
|}) {
  const { failedValidation } = usePropsValidation(props, EANExtensionSkills);
  if (failedValidation) return null;
  return (
    <div style={{ paddingTop: 12 }}>
      <SubHeading>Could extend</SubHeading>
      <div style={{ height: 6 }} />
      {props.children}
    </div>
  );
}
EANExtensionSkills.spec = {
  children: t.childrenArray(t.elementType(EANSkill)),
};

export function EANSkill(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANSkill);
  if (failedValidation) return null;
  return (
    <div
      style={{
        background: productColor.seashell,
        borderRadius: 8,
        margin: '6px 0',
        padding: 20,
      }}
    >
      {props.children}
    </div>
  );
}
EANSkill.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};
